import type { BuildRoofEdgesOptions } from "building-generation/elements/roofs/roofEdges";
import { buildRoofEdges, buildExactRoofEdges } from "building-generation/elements/roofs/roofEdges";
import { offsetRoofPerimeter } from "building-generation/elements/roofs/roofPerimeters";
import { getFirstSlope } from "building-generation/elements/roofs/roofSlopes";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { RoofPerimeters } from "building-types/settings/roofTypes";
import { EaveCutType, GableRoofType } from "building-types/settings/settingsTypesEnum";
import { offsetPolyLine2Variable } from "geom/offset";
import { polyLine2Copy } from "geom/polyLine2";

/**
 * Generates the edge information for a multi-gable roof.
 * This includes the side (rectangular shaped) edges, the exact edges (can be non-rectangular),
 * and the eaves edges, used by the old trim generation system (TODO: Deprecate them soon)
 */
export function generateRoofVolumeEdges({
  roofVolumeOptions,
  roofVolume,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    id: subRoofId,
    baseHeight,
    roofType,
    leftSideSlopes,
    rightSideSlopes,
    leftSpan,
    rightSpan,
    heelHeightOffsets,
    rails2D,
    perimeters,
    extensions,
    verticalRails,
    rectangularOverhangs,
    overhangs,
    shedExtensions,
    exteriorWallOffsetsWithFinish,
    rectangularExtWallOffsetWithFinish,
    exteriorWallOffsetsFromOverhangDatum,
    rectangularExtWallOffsetsFromOverhangDatum,
  } = roofVolume;

  const { roofData, inputPerimeters, insertedRoofVolumes, startEdge, roofInputSettings } = roofVolumeOptions;
  const { roofSettings, roofFramingSettings } = roofInputSettings;

  const { roofEdgeOverrides, roofSideOverrides } = roofData;
  const { exactPerimeter } = inputPerimeters;
  const { sideExtension } = shedExtensions;
  const { boundingPerimeter, offsetBoundingPerimeter, offsetExactPerimeter } = perimeters;
  const { peakRail } = rails2D;
  const { edgeOverhangs } = overhangs;

  const {
    startBaseExtension,
    endBaseExtension,
    isStartFullyTruncated,
    isEndFullyTruncated,
    truncatedStartSlopes,
    truncatedEndSlopes,
  } = verticalRails;

  const {
    eaveType,
    eaveCutType = EaveCutType.PLUMB,
    eaveExtensionAtCoplanarWall = 0,
    eaveExtensionAtNonCoplanarRake = 0,
  } = roofSettings;

  const { slope: firstSlopeLeft } = getFirstSlope(leftSideSlopes);
  const { slope: firstSlopeRight } = getFirstSlope(rightSideSlopes);

  const firstSlopeStart = getFirstSlope(truncatedStartSlopes).slope;
  const firstSlopeEnd = getFirstSlope(truncatedEndSlopes).slope;

  const eaveSlope = Math.min(
    firstSlopeLeft,
    firstSlopeRight,
    isStartFullyTruncated ? firstSlopeStart : Number.POSITIVE_INFINITY,
    isEndFullyTruncated ? firstSlopeEnd : Number.POSITIVE_INFINITY,
  );

  // edge info
  const roofEdgeOptions: BuildRoofEdgesOptions = {
    boundingPerimeter,
    offsetBoundingPerimeter,
    exactPerimeter,
    offsetExactPerimeter,
    startEdge,
    subRoofId,
    baseHeight,
    leftSpan,
    rightSpan,
    heelHeightOffsets,
    rectangularOverhangs,
    edgeExteriorWallOffsetWithFinish: rectangularExtWallOffsetWithFinish,
    edgeExteriorWallOffsetFromOverhangDatum: rectangularExtWallOffsetsFromOverhangDatum,
    edgeOverhangs,
    eaveSlope,
    leftSideSlopes,
    rightSideSlopes,
    startSlopes: truncatedStartSlopes,
    endSlopes: truncatedEndSlopes,
    peakRail,
    fullyTruncatedStart: isStartFullyTruncated,
    fullyTruncatedEnd: isEndFullyTruncated,
    roofType,
    extensions,
    shedExtensions,
    eaveType,
    eaveCutType,
    roofSideOverrides,
    eaveExtensionAtCoplanarWall,
    eaveExtensionAtNonCoplanarRake,
    roofSettings,
    roofFramingSettings,
  };

  const edges = buildRoofEdges(roofEdgeOptions);

  // exact edge info
  const exactRoofEdgeOptions: BuildRoofEdgesOptions = {
    boundingPerimeter,
    offsetBoundingPerimeter,
    exactPerimeter,
    offsetExactPerimeter,
    startEdge,
    subRoofId,
    baseHeight,
    leftSpan,
    rightSpan,
    heelHeightOffsets,
    rectangularOverhangs,
    edgeExteriorWallOffsetWithFinish: exteriorWallOffsetsWithFinish,
    edgeExteriorWallOffsetFromOverhangDatum: exteriorWallOffsetsFromOverhangDatum,
    edgeOverhangs,
    eaveSlope,
    leftSideSlopes,
    rightSideSlopes,
    startSlopes: truncatedStartSlopes,
    endSlopes: truncatedEndSlopes,
    peakRail,
    fullyTruncatedStart: isStartFullyTruncated,
    fullyTruncatedEnd: isEndFullyTruncated,
    roofType,
    extensions,
    shedExtensions,
    eaveType,
    eaveCutType,
    roofEdgeOverrides,
    roofSideOverrides,
    eaveExtensionAtCoplanarWall,
    eaveExtensionAtNonCoplanarRake,
    roofSettings,
    roofFramingSettings,
  };

  const exactEdges = buildExactRoofEdges(exactRoofEdgeOptions);

  // build wall perimeter
  const wallOffsets: number[] = new Array<number>(exactEdges.length).fill(0);
  for (let i = 0, l = exactEdges.length; i < l; i++) {
    const { isStartEdge, isEndEdge, isLeftEdge, isRightEdge } = exactEdges[i];
    if (isStartEdge) {
      wallOffsets[i] = Math.max(startBaseExtension, 0);
    } else if (isEndEdge) {
      wallOffsets[i] = Math.max(endBaseExtension, 0);
    } else if (isRightEdge && roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
      wallOffsets[i] = sideExtension;
    } else if (isLeftEdge && roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) {
      wallOffsets[i] = sideExtension;
    }
  }

  const exactPerimeterExtended = offsetPolyLine2Variable(exactPerimeter, wallOffsets);
  const wallPerimeter = offsetPolyLine2Variable(exactPerimeterExtended, exteriorWallOffsetsWithFinish);
  const offsetWallPerimeter = offsetRoofPerimeter(exactPerimeterExtended, edgeOverhangs);
  const wallsPerimeterWRTOverhangDatum = offsetPolyLine2Variable(exactPerimeter, exteriorWallOffsetsFromOverhangDatum);

  const newPerimeters: RoofPerimeters = {
    ...perimeters,
    wallPerimeter,
    offsetWallPerimeter,
    eavesPerimeter: polyLine2Copy(wallPerimeter),
    wallsPerimeterWRTOverhangDatum,
  };

  // eaves edge info
  const eaveEdgesOptions: BuildRoofEdgesOptions = {
    boundingPerimeter,
    offsetBoundingPerimeter,
    exactPerimeter: exactPerimeterExtended,
    offsetExactPerimeter,
    startEdge,
    subRoofId,
    baseHeight,
    leftSpan,
    rightSpan,
    heelHeightOffsets,
    rectangularOverhangs,
    edgeOverhangs,
    edgeExteriorWallOffsetWithFinish: exteriorWallOffsetsWithFinish,
    edgeExteriorWallOffsetFromOverhangDatum: exteriorWallOffsetsFromOverhangDatum,
    eaveSlope,
    leftSideSlopes,
    rightSideSlopes,
    startSlopes: truncatedStartSlopes,
    endSlopes: truncatedEndSlopes,
    peakRail,
    fullyTruncatedStart: isStartFullyTruncated,
    fullyTruncatedEnd: isEndFullyTruncated,
    roofType,
    eaveType,
    extensions,
    shedExtensions,
    eaveCutType,
    insertedRoofVolumes,
    roofEdgeOverrides,
    roofSideOverrides,
    eaveExtensionAtCoplanarWall,
    eaveExtensionAtNonCoplanarRake,
    roofSettings,
    roofFramingSettings,
  };

  const eaveEdges = buildExactRoofEdges(eaveEdgesOptions);

  return {
    roofVolume: {
      edges,
      exactEdges,
      eaveEdges,
      perimeters: newPerimeters,
    },
  };
}
